import type { ReferralDecodeResponse } from '@otter-ai/recall-client';
import * as recallApi from '@otter-ai/recall-client';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import queryString from 'query-string';
import type { AdditionalQueryOptions } from '@/shared/utils/queryCreators';

const referralDecodeQueryKey = (referralCode: string) =>
  ['referral_decode', referralCode] as const;

type ReferralDecodeQueryKey = ReturnType<typeof referralDecodeQueryKey>;

type AdditionalReferralDecodeQueryOptions = AdditionalQueryOptions<
  ReferralDecodeResponse,
  ReferralDecodeQueryKey
>;

export type ReferralDecodeQuery = {
  readonly queryKey: ReferralDecodeQueryKey;
  readonly queryFn: () => Promise<ReferralDecodeResponse>;
} & AdditionalReferralDecodeQueryOptions;

export function referralDecodeQueryCreator({
  referralCode,
  additionalOptions,
}: {
  referralCode: string;
  additionalOptions?: AdditionalReferralDecodeQueryOptions;
}): ReferralDecodeQuery {
  return {
    queryKey: referralDecodeQueryKey(referralCode),
    queryFn: async () => {
      const secret = process.env.NEXT_PUBLIC_REFERRAL_DECODE_SIGNIN_SECRET;

      if (!secret) {
        throw new Error('/referral_decode signin secret is not configured.');
      }

      const ts = (Date.now() / 1000).toFixed(0);
      const algorithm = 'AS2-HMAC-SHA256';

      const message = queryString.stringify({
        algorithm,
        ts,
        // Todo(Hao): api returns error if appid is set to 'otter-next'
        appid: 'otter-web',
      });

      const signature = hmacSHA256(message, secret).toString();

      const body = {
        referral_code: referralCode,
        // Todo(Hao): api returns error if appid is set to 'otter-next'
        appid: 'otter-web' as recallApi.AppId,
        algorithm,
        ts,
        signature,
      };

      const response = await recallApi.referralDecode({
        body,
        throwOnError: true,
      });

      return response.data;
    },
    ...additionalOptions,
  };
}
